import { CommandNotHandledError } from './CommandNotHandledError';
/**
 * ChatControlAPI is a class that provides a control interface for a specific chat instance.
 * It allows for setting custom variables, registering listeners, and triggering commands.
 */
export class ChatControlAPI {
    /**
     * Constructor for ChatControlAPI.
     *
     * @param globalApi - The global instance of GlobalChatAPI.
     * @param chatId - The unique identifier for the chat instance.
     */
    constructor(globalApi, chatId, chatClient) {
        this.globalApi = globalApi;
        this.chatId = chatId;
        this.chatClient = chatClient;
        this._command = {};
        this._customVariables = {};
        this._listeners = new Map();
    }
    /**
     * Override custom variables for the chat instance.
     *
     * @param variables - An object containing extra variables for message payloads
     */
    setCustomVariables(variables) {
        this._customVariables = variables;
    }
    /**
     * Add custom variables to the chat instance.
     *
     * @param variables - An object containing extra variables for message payloads
     */
    addCustomVariables(variables) {
        this._customVariables = Object.assign(Object.assign({}, this._customVariables), variables);
    }
    /**
     * Set a custom variable for the chat instance.
     *
     * @param key - The key of the variable to set
     * @param value - The value of the variable to set
     */
    setCustomVariable(key, value) {
        this._customVariables[key] = value;
    }
    /**
     * Delete custom variables from the chat   instance.
     *
     * @param keys - An array of keys to delete from the custom variables
     */
    deleteCustomVariables(keys) {
        keys.forEach((key) => {
            delete this._customVariables[key];
        });
    }
    /**
     * Delete a custom variable from the chat instance.
     *
     * @param key - The key of the variable to delete
     */
    deleteCustomVariable(key) {
        delete this._customVariables[key];
    }
    /**
     * Register a listener for a specific command in the chat instance.
     *
     * The callback's argument and return type are strongly derived from the registered command's handler.
     *
     * @param commandName - The name of the command to listen for
     * @param callback - The callback function to execute when the command is triggered
     */
    registerListener(commandName, callback) {
        // Retrieve existing listeners for the command
        const listeners = this._listeners.get(commandName) || [];
        // Type assertion is used here because _listeners is loosely typed
        if (!listeners.includes(callback)) {
            listeners.push(callback);
            this._listeners.set(commandName, listeners);
        }
    }
    /**
     * Unregister a listener for a specific command in the chat instance.
     *
     * @param commandName - The name of the command to unregister the listener from
     * @param callback - The callback function to remove from the listener list
     */
    unregisterListener(commandName, callback) {
        const listeners = this._listeners.get(commandName) || [];
        listeners.splice(listeners.indexOf(callback), 1);
        this._listeners.set(commandName, listeners);
    }
    /**
     * Trigger a command for the chat instance.
     *
     * The args and return type are safely derived from the registered command's handler.
     *
     * @param commandName - The name of the command to trigger
     * @param args - The arguments for the command as defined in the command's handler
     * @returns A promise resolving with the result of the command execution
     */
    async triggerCommand(commandName, args) {
        const listeners = this._listeners.get(commandName) || [];
        for (const listener of listeners) {
            const result = await listener(args);
            if (result) {
                return result;
            }
        }
        // Instead of returning Promise.reject, throw a specialized error.
        throw new CommandNotHandledError(String(commandName));
    }
    /**
     * Register commands for this chat instance.
     *
     * @param commands - The strongly typed command registry for this chat instance.
     */
    registerCommands(commands) {
        this._command = commands;
    }
    /**
     * Get the commands registered for this chat instance.
     *
     * @returns The command registry for this chat instance.
     */
    get commands() {
        return this._command;
    }
    /**
     * Get the custom variables for this chat instance.
     *
     * @returns The custom variables for this chat instance.
     */
    get customVariables() {
        return this._customVariables;
    }
    /**
     * Retrieves the custom variables cast to a specified interface.
     *
     * @returns The custom variables as type T.
     */
    getTypedCustomVariables() {
        return this._customVariables;
    }
    /**
     * Add a preprocessor plugin to the chat client
     */
    addPreprocessorPlugin(plugin) {
        this.chatClient.addPreprocessorPlugin(plugin);
    }
}
//# sourceMappingURL=ChatControlAPI.js.map